import { useTheme } from "../../theme/useTheme"
import { ColorSchemeToggle } from "../ui/Toggles"
import { Close, Menu } from "@mui/icons-material"
import { Box, IconButton, Link } from "@mui/material"
import React from "react"

interface TopNavProps {
    onToggleSidebar: () => void
    sidebarOpen: boolean
    isMediumScreen: boolean
}

// fixed, not sticky — fixed survives the horizontal document scroll below
// the sheet's minimum rendered width
export const TOP_NAV_HEIGHT = 60

// slim header mirroring the portfolio's Nav.astro — text-only wordmark
// (formal register, no fox head). Chrome pins to the viewport corners
// (Google Docs pattern): the sheet's centerline shifts with every
// breakpoint, so sheet-anchored chrome floated over empty background
export const TopNav: React.FC<TopNavProps> = ({ onToggleSidebar, sidebarOpen, isMediumScreen }) => {
    const { themeSettings } = useTheme()

    return (
        <Box
            className="no-print"
            component="nav"
            sx={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                // above the drawer's modal layer — the drawer slides under the bar
                zIndex: 1400,
                backdropFilter: "blur(8px)",
                // solid while the drawer scrim is up: the frosted bar is
                // translucent and would pick up the dark backdrop behind it
                background:
                    isMediumScreen && sidebarOpen
                        ? themeSettings.backgroundColor
                        : `color-mix(in srgb, ${themeSettings.backgroundColor} 80%, transparent)`,
                borderBottom: "1px solid",
                borderColor: "divider",
                // viewport-fit=cover lets the notch overlap the fixed bar;
                // inset is 0 on unnotched devices
                paddingTop: "env(safe-area-inset-top)",
            }}
        >
            <Box
                sx={{
                    px: 3,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    // the nav grows by the notch inset, so the row grows with it
                    minHeight: 60,
                }}
            >
                <Link
                    href="https://wisl.dev"
                    // same href as the contacts' wisl.dev links — axe
                    // identical-links-same-purpose wants one name per destination
                    aria-label="wisl.dev"
                    sx={{
                        fontFamily: '"Fraunces", Georgia, serif',
                        fontWeight: 500,
                        fontVariationSettings: '"SOFT" 100',
                        fontSize: 21,
                        letterSpacing: "-0.02em",
                        color: "text.primary",
                        textDecoration: "none",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "0.26em",
                        userSelect: "none",
                        "&:hover": { color: "text.primary" },
                        // period is the only orange
                        "& .dot": { color: "primary.main" },
                    }}
                >
                    <span>
                        wisl<span className="dot">.</span>
                    </span>
                </Link>
                <Box sx={{ display: "flex", alignItems: "center", userSelect: "none" }}>
                    <ColorSchemeToggle size={48} />
                    {isMediumScreen && (
                        <>
                            <Box sx={{ width: 1, alignSelf: "stretch", bgcolor: "divider" }} />
                            <IconButton
                                onClick={onToggleSidebar}
                                aria-label={sidebarOpen ? "Close sidebar" : "Open sidebar"}
                                sx={{
                                    width: 48,
                                    height: 48,
                                    borderRadius: 0,
                                    // kill MUI's own action.hover fill explicitly
                                    "&:hover": { bgcolor: "transparent" },
                                }}
                            >
                                {sidebarOpen ? <Close /> : <Menu />}
                            </IconButton>
                        </>
                    )}
                </Box>
            </Box>
        </Box>
    )
}
